import React from 'react';
import { Link } from 'react-router-dom';
import { Quote } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Testimonials = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation(0.2);
  const [photoRef, photoVisible] = useScrollAnimation(0.2);
  const [ctaRef, ctaVisible] = useScrollAnimation(0.2);

  const testimonials = [
    {
      id: "testimonial-1",
      quote: "Hey Angie, we all had a lovely time yesterday at your event, it was good to see and for all the kids together meet. You have a lovely family. Good luck in all future ventures",
      author: "Daphne Pratt",
      title: "A wonderful family gathering"
    },
    {
      id: "testimonial-2",
      quote: "Lovely event. We thoroughly enjoyed the day/evening - so glad we could make it. Well done lil girlfriend",
      author: "Dot Wak-Williams",
      title: "Thoroughly enjoyed"
    },
    {
      id: "testimonial-3",
      quote: "Aunty Ange you done soooo well, literally the cutest day, thank you for having us, everything was perfect, from the laughter, dances, food, energy... top tier lux event!",
      author: "Anneka",
      title: "Top tier lux event"
    },
    {
      id: "testimonial-4",
      quote: "I felt at home, the food was splendid, I felt a part of the family. For a first event it was more then I expected and hats off to the organisers and those part of the event. I really enjoyed my time and I think it's highly recommendable to all crowds who doesn't like food around good energy and love?",
      author: "Thyreece Ewart",
      title: "Felt like family"
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-bg-zoom" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/tqhnalfr_IMG_0101.jpeg')", backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="testimonials-hero">
          <h1 className="text-6xl md:text-7xl font-bold text-shadow-luxury" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>What Our Guests Say</h1>
          <div className="w-24 h-1 bg-gold mx-auto mt-4"></div>
          <p className="text-2xl mt-6 italic">Hear from the Mama-Mam's Family</p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section ref={testimonialsRef} className="py-24 px-4 bg-cream-dark" data-testid="testimonials-grid">
        <div className={`max-w-6xl mx-auto ${testimonialsVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="w-16 h-1 bg-gold mx-auto mb-16"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="bg-white p-10 rounded-lg shadow-luxury-hover transition-all relative"
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center shadow-lg">
                    <Quote className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-brown mb-6 mt-6 text-center" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>{testimonial.title}</h3>
                <p className="text-gray-700 italic leading-relaxed mb-8 text-center">"{testimonial.quote}"</p>
                <div className="border-t border-gold pt-6">
                  <p className="font-semibold text-brown text-center">— {testimonial.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Testimonials */}
      <section ref={photoRef} className="py-16 px-4 bg-white">
        <div className={`max-w-5xl mx-auto ${photoVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          {/* Featured Family Photo */}
          <div className="mb-16 max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-lg shadow-luxury bg-cream-dark">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/6vz4e3sz_IMG_0126.jpeg" 
                alt="Group celebration at Mama-Mam's Kitchen" 
                className="w-full h-[600px] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-8 text-white">
                <p className="text-2xl italic mb-2">"Creating beautiful memories, one Sunday at a time"</p>
                <p className="font-bold text-gold">— Our Mama-Mam's Family</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-gold text-white text-center">
        <div className={`${ctaVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Become Part of Our Story</h2>
          <p className="text-xl mb-8 opacity-90">Join us for an unforgettable Sunday experience</p>
        </div>
        <div className={`${ctaVisible ? 'animate-scale-in delay-200' : 'opacity-0'}`}>
          <Link 
            to="/reserve" 
            className="inline-block bg-white text-brown hover:bg-cream font-semibold px-10 py-4 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg"
            data-testid="testimonials-cta-button"
          >
            Reserve Your Table Now →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
